import { useSignal } from "@preact/signals";
import dayjs from "dayjs";
import { useEffect, useRef } from "preact/hooks";
import type { Video } from "@/models/video";
import type {
  DayActivity,
  WatchHistory,
  WatchTime,
} from "@/models/watchHistory";
import {
  getUserWatchHistory as _getUserWatchHistory,
  upsertVideoWatch as _upsertVideoWatch,
} from "@/services";

export const WATCH_HISTORY_KEY = "userWatchHistory";

type TrackVideoProps = {
  id?: number;
  percent: number;
  seconds: number;
  video: Video;
  timeZone: string | null | undefined;
  completed: boolean;
};

interface WatchHistoryHook {
  userActivity: WatchHistory;
  trackVideo(props: TrackVideoProps): void;
  useFetchUserWatchHistory(
    startDate: dayjs.Dayjs,
    endDate: dayjs.Dayjs,
    refetch?: boolean,
    isDelete?: boolean,
  ): WatchHistory | null | undefined;
  lastDaysOfHistory(daysAgo: number): WatchHistory;
}

const emptyState = {
  timeByDay: {},
  watchHistory: [],
};

export function useWatchHistory(): WatchHistoryHook {
  const userActivity = useSignal<WatchHistory>(readWatchHistoryCache());

  const trackVideo = ({
    id,
    percent,
    seconds,
    timeZone,
    video,
    completed,
  }: TrackVideoProps): void => {
    try {
      const d = dayjs();
      const dateStr = d.format("YYYY-MM-DD");
      const watchedAt = d.valueOf();
      const whEntry: DayActivity = {
        video,
        watchedAt,
        seconds: Math.round(seconds),
        percent,
        date: dateStr,
        watchTimes: {
          [watchedAt]: { timeZone, seconds } as WatchTime,
        },
      };
      const wh = {
        watchHistory: [whEntry],
        timeByDay: { [dateStr]: seconds },
      };
      const mwh = mergeWatchHistory(wh, userActivity.value);
      cacheWatchHistory(mwh);
      userActivity.value = mwh;

      _upsertVideoWatch({
        id,
        videoId: video.id,
        percent,
        seconds,
        completed,
      });
    } catch (_error) {}
  };

  const useFetchUserWatchHistory = (
    startDate = dayjs().startOf("month"),
    endDate = dayjs(),
    refetch = false,
    deleteActivity = false,
  ) => {
    const fetchingRef = useRef(false);

    // Use stable string keys so the effect doesn't re-fire on every render
    const startKey = startDate.format("YYYY-MM-DD");
    const endKey = endDate.format("YYYY-MM-DD");

    useEffect(() => {
      if (fetchingRef.current) return;
      fetchingRef.current = true;

      (async () => {
        try {
          let effectEndDate = endDate;
          if (effectEndDate.isAfter(dayjs())) {
            effectEndDate = dayjs();
          }
          const result = await _getUserWatchHistory(startDate, effectEndDate);
          const watchHistory = result?.watchHistory ? result : emptyState;
          const mergedWatchHistory = mergeWatchHistory(
            watchHistory,
            userActivity.value,
          );

          if (deleteActivity) {
            cacheWatchHistory(watchHistory);
            userActivity.value = watchHistory;
          } else {
            cacheWatchHistory(mergedWatchHistory);
            userActivity.value = mergedWatchHistory;
          }
        } catch (_error) {
          // noop
        } finally {
          fetchingRef.current = false;
        }
      })();
    }, [startKey, endKey, refetch]);

    return userActivity.value;
  };

  const lastDaysOfHistory = (daysAgo: number) => {
    if (readWatchHistoryCache()) {
      const d = dayjs().subtract(daysAgo, "days");
      return {
        timeByDay: {},
        watchHistory: readWatchHistoryCache()?.watchHistory.filter((w) =>
          dayjs(w.date).isAfter(d),
        ),
      };
    }
    return emptyState;
  };

  return {
    userActivity: userActivity.value,
    trackVideo,
    useFetchUserWatchHistory,
    lastDaysOfHistory,
  };
}

export function cacheWatchHistory(wh: WatchHistory): void {
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(WATCH_HISTORY_KEY, JSON.stringify(wh));
    } catch (_err) {}
  }
}

function readWatchHistoryCache(): WatchHistory {
  if (typeof window !== "undefined") {
    const cwh = localStorage.getItem(WATCH_HISTORY_KEY);
    const watchHistory = cwh ? JSON.parse(cwh) : emptyState;
    return watchHistory;
  }
  return emptyState;
}

const mergeWatchHistory = (
  updatedHistory: WatchHistory,
  existingHistory: WatchHistory,
): WatchHistory => {
  if (updatedHistory?.watchHistory.length === 0) {
    return existingHistory;
  } else if (
    updatedHistory &&
    Object.keys(updatedHistory?.timeByDay)?.length > 0 &&
    Object.keys(existingHistory?.timeByDay)?.length &&
    dayjs(Object.keys(updatedHistory.timeByDay)[0]).isBefore(
      Object.keys(existingHistory.timeByDay)[0],
    )
  ) {
    return {
      timeByDay: {
        ...updatedHistory.timeByDay,
        ...existingHistory.timeByDay,
      },
      watchHistory: [
        ...updatedHistory.watchHistory,
        ...existingHistory.watchHistory,
      ],
    };
  } else if (
    updatedHistory &&
    Object.keys(updatedHistory?.timeByDay)?.length > 0 &&
    Object.keys(existingHistory?.timeByDay)?.length
  ) {
    return {
      timeByDay: {
        ...existingHistory.timeByDay,
        ...updatedHistory.timeByDay,
      },
      watchHistory: [
        ...existingHistory.watchHistory,
        ...updatedHistory.watchHistory,
      ],
    };
  }
  return updatedHistory;
};
